import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CHAT_DEEP_LINK_EVENT, DEEP_LINK_MESSAGE_TYPE } from "../constants";
import {
  isProtectedDeepLinkPath,
  normalizeDeepLinkPath,
  resolvePushDataToPath,
} from "../utils/deepLink";

type DeepLinkMessage = {
  type?: string;
  path?: string;
};

export function useDeepLinkNavigation(enabled: boolean) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    const navigateToPath = (rawPath: string | null | undefined) => {
      const path = normalizeDeepLinkPath(rawPath);
      if (!path || !isProtectedDeepLinkPath(path)) {
        return;
      }
      navigate(path);
    };

    const handleServiceWorkerMessage = (event: MessageEvent<DeepLinkMessage>) => {
      const data = event.data;
      if (!data || data.type !== DEEP_LINK_MESSAGE_TYPE) {
        return;
      }
      navigateToPath(data.path);
    };

    const handleChatDeepLinkEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ path?: string }>;
      navigateToPath(customEvent.detail?.path);
    };

    navigator.serviceWorker?.addEventListener(
      "message",
      handleServiceWorkerMessage
    );
    window.addEventListener(CHAT_DEEP_LINK_EVENT, handleChatDeepLinkEvent);

    return () => {
      navigator.serviceWorker?.removeEventListener(
        "message",
        handleServiceWorkerMessage
      );
      window.removeEventListener(CHAT_DEEP_LINK_EVENT, handleChatDeepLinkEvent);
    };
  }, [enabled, navigate]);
}

export function navigateFromPushPayload(
  navigate: ReturnType<typeof useNavigate>,
  data: Record<string, string> | null | undefined
): boolean {
  const path = resolvePushDataToPath(data);
  if (!path) {
    return false;
  }
  navigate(path);
  return true;
}
